import { Calendar } from '@griply/assets';
import { getSmartDateLabel } from '@griply/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleDateField } from 'shared/components/ui/schedule-date-field';
import { TimePickerInput } from 'shared/components/ui/time-picker-input';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { parseDateTime } from 'shared/utils/parse-date-time';

import * as Styled from '../event-form.style';
import { EventRow } from '../event-row';

export type EventDateTimeRowProps = {
  label: string;
  date: Date;
  time: number | null;
  allDay: boolean;
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  timePlaceholder: string;
  minDate?: Date;
  disabled?: boolean;
  onChangeDate?: (date: Date | null) => void;
  onChangeTime?: (time?: number) => void;
};

export const EventDateTimeRow: React.FC<EventDateTimeRowProps> = ({
  label,
  date,
  time,
  allDay,
  today,
  weekStartsOn,
  dateFormat,
  timeFormat,
  timePlaceholder,
  minDate,
  disabled,
  onChangeDate,
  onChangeTime,
}) => {
  const { t } = useTranslation();

  const isReadOnly = !!disabled || !onChangeDate || !onChangeTime;

  if (isReadOnly) {
    const dateLabel = getSmartDateLabel(date, today, t, {
      enableWeekdays: true,
    });
    const readOnlyLabel =
      allDay || time === null
        ? dateLabel
        : `${dateLabel}, ${parseDateTime(time, timeFormat)}`;

    return (
      <EventRow icon={Calendar} label={label}>
        <Styled.ReadOnlyValue>{readOnlyLabel}</Styled.ReadOnlyValue>
      </EventRow>
    );
  }

  return (
    <EventRow icon={Calendar} label={label}>
      <Styled.DateTimeFields>
        <ScheduleDateField
          value={date}
          onChange={onChangeDate}
          today={today}
          weekStartsOn={weekStartsOn}
          dateFormat={dateFormat}
          minDate={minDate}
        />
        {!allDay && (
          <TimePickerInput
            value={time ?? undefined}
            timeFormat={timeFormat}
            onChange={onChangeTime}
            placeholder={timePlaceholder}
            showAsFormInput
            fullWidth
          />
        )}
      </Styled.DateTimeFields>
    </EventRow>
  );
};
